/**
 * Business Account Users Service
 *
 * API client for managing business account users, profiles, and roles.
 *
 * @module features/users/services/businessAccountUsers
 */

import client from '@/shared/api/client';
import { isMockMode } from '@/__dev__/isMockMode';
import {
  mockListBusinessAccountUsers,
  mockGetExternalProfiles,
  mockAssignRole,
  mockRemoveRole,
  mockDeleteUser,
} from '@/__dev__/mocks/businessAccountUsers.mock';
import type {
  BusinessAccountUsersResponse,
  ListUsersParams,
  ExternalProfile,
  AssignRoleRequest,
  AssignRoleResponse,
  RemoveRoleRequest,
  RemoveRoleResponse,
} from '@/features/users/types/users.types';

/**
 * Lists users with access to the current business account (paginated)
 *
 * @param params - Pagination parameters
 * @returns Promise with paginated business account users
 *
 * @example
 * ```typescript
 * const users = await listBusinessAccountUsers({ offset: 0, limit: 10 });
 * console.log(users.content); // Array of users
 * console.log(users.page.totalElements); // Total count
 * ```
 */
export async function listBusinessAccountUsers(
  params: ListUsersParams = {}
): Promise<BusinessAccountUsersResponse> {
  if (isMockMode) return mockListBusinessAccountUsers(params);
  const { offset = 0, limit = 10, ...filters } = params;
  const { data } = await client.get<BusinessAccountUsersResponse>(
    '/users/business-account-users',
    {
      params: { offset, limit, ...filters },
    }
  );
  return data;
}

/**
 * Gets a list of external profiles available for assignment
 *
 * Returns only profiles with level greater than the logged-in user's profile level.
 *
 * @returns Promise with array of external profiles
 *
 * @example
 * ```typescript
 * const profiles = await getExternalProfiles();
 * // Filter for specific profile types
 * const managerProfiles = profiles.filter(p => p.name.includes('Manager'));
 * ```
 */
export async function getExternalProfiles(): Promise<ExternalProfile[]> {
  if (isMockMode) return mockGetExternalProfiles();
  const { data } = await client.get<ExternalProfile[]>('/profiles/external');
  return data;
}

/**
 * Assigns a role/profile to a user
 *
 * Removes all previous roles except the new one.
 *
 * @param payload - User ID and role name to assign
 * @returns Promise with success message
 *
 * @example
 * ```typescript
 * const result = await assignRole({
 *   userId: 'user-123',
 *   roleName: 'ROLE_EXTERNAL_MANAGER'
 * });
 * console.log(result.message); // "Role assigned successfully"
 * ```
 */
export async function assignRole(
  payload: AssignRoleRequest
): Promise<AssignRoleResponse> {
  if (isMockMode) return mockAssignRole(payload);
  const { data } = await client.post<AssignRoleResponse>(
    '/user-roles/add',
    payload
  );
  return data;
}

/**
 * Removes a role/profile from a user
 *
 * @param payload - User ID and role name to remove
 * @returns Promise with success message
 *
 * @example
 * ```typescript
 * const result = await removeRole({
 *   userId: 'user-123',
 *   roleName: 'ROLE_EXTERNAL_MANAGER'
 * });
 * console.log(result.message); // "Role removed successfully"
 * ```
 */
export async function removeRole(
  payload: RemoveRoleRequest
): Promise<RemoveRoleResponse> {
  if (isMockMode) return mockRemoveRole(payload);
  const { data } = await client.post<RemoveRoleResponse>(
    '/user-roles/remove',
    payload
  );
  return data;
}

/**
 * Deletes a user from the business account
 *
 * @param userId - ID of the user to delete
 * @returns Promise that resolves on success
 *
 * @example
 * ```typescript
 * await deleteUser('user-123');
 * // User is now deleted
 * ```
 */
export async function deleteUser(userId: string): Promise<void> {
  if (isMockMode) return mockDeleteUser(userId);
  await client.delete(`/users/user-account/${userId}`);
}
